import {
  Clock,
  AlertCircle,
  MessageSquare,
  CheckCircle,
  X,
  Flame,
  Zap,
  AlertTriangle,
  Info,
  CircleDot,
  type LucideIcon,
} from "lucide-react";
import i18n from "@/i18n";
import { getIntlLocale } from "@/i18n";
import type { BadgeVariant } from "@/components/ui/badge-variants";

// Ticket Status enum-like constants
export const TicketStatus = {
  OPEN: "open",
  IN_PROGRESS: "in_progress",
  WAITING_RESPONSE: "waiting_response",
  RESOLVED: "resolved",
  CLOSED: "closed",
} as const;
export type TicketStatus = (typeof TicketStatus)[keyof typeof TicketStatus];

// Ticket Priority enum-like constants
export const TicketPriority = {
  CRITICAL: 1,
  HIGH: 2,
  MEDIUM: 3,
  LOW: 4,
  TRIVIAL: 5,
} as const;
export type TicketPriority =
  (typeof TicketPriority)[keyof typeof TicketPriority];

// Ticket Category enum-like constants
export const TicketCategory = {
  PROBLEM: "problem",
  SUGGESTION: "suggestion",
  IMPROVEMENT: "improvement",
  QUESTION: "question",
  BILLING: "billing",
} as const;
export type TicketCategory =
  (typeof TicketCategory)[keyof typeof TicketCategory];

// Status Configuration
export interface StatusConfig {
  labelKey: string;
  icon: LucideIcon;
  color: string;
  bgColor: string;
  iconColor: "primary" | "accent" | "success" | "warning" | "error" | "default";
  badgeVariant: BadgeVariant;
}

export const STATUS_CONFIG: Record<TicketStatus, StatusConfig> = {
  open: {
    labelKey: "status.open",
    icon: AlertCircle,
    color: "text-primary-600",
    bgColor: "bg-primary-50",
    iconColor: "primary",
    badgeVariant: "primary",
  },
  in_progress: {
    labelKey: "status.in_progress",
    icon: Clock,
    color: "text-accent-600",
    bgColor: "bg-accent-50",
    iconColor: "accent",
    badgeVariant: "accent",
  },
  waiting_response: {
    labelKey: "status.waiting_response",
    icon: MessageSquare,
    color: "text-warning-600",
    bgColor: "bg-warning-50",
    iconColor: "warning",
    badgeVariant: "warning",
  },
  resolved: {
    labelKey: "status.resolved",
    icon: CheckCircle,
    color: "text-success-600",
    bgColor: "bg-success-50",
    iconColor: "success",
    badgeVariant: "success",
  },
  closed: {
    labelKey: "status.closed",
    icon: X,
    color: "text-surface-500",
    bgColor: "bg-surface-100",
    iconColor: "default",
    badgeVariant: "default",
  },
};

// Priority Configuration
export interface PriorityConfig {
  labelKey: string;
  icon: LucideIcon;
  color: string;
  bgColor: string;
  badgeVariant: BadgeVariant;
}

export const PRIORITY_CONFIG: Record<TicketPriority, PriorityConfig> = {
  1: {
    labelKey: "priority.1",
    icon: Flame,
    color: "text-error-600",
    bgColor: "bg-error-50",
    badgeVariant: "error",
  },
  2: {
    labelKey: "priority.2",
    icon: Zap,
    color: "text-warning-600",
    bgColor: "bg-warning-50",
    badgeVariant: "warning",
  },
  3: {
    labelKey: "priority.3",
    icon: AlertTriangle,
    color: "text-accent-600",
    bgColor: "bg-accent-50",
    badgeVariant: "warning",
  },
  4: {
    labelKey: "priority.4",
    icon: Info,
    color: "text-surface-600",
    bgColor: "bg-surface-100",
    badgeVariant: "default",
  },
  5: {
    labelKey: "priority.5",
    icon: CircleDot,
    color: "text-surface-400",
    bgColor: "bg-surface-50",
    badgeVariant: "default",
  },
};

// Category Configuration
export interface CategoryConfig {
  labelKey: string;
  emoji: string;
}

export const CATEGORY_CONFIG: Record<TicketCategory, CategoryConfig> = {
  problem: { labelKey: "category.problem", emoji: "🔧" },
  suggestion: { labelKey: "category.suggestion", emoji: "💡" },
  improvement: { labelKey: "category.improvement", emoji: "📈" },
  question: { labelKey: "category.question", emoji: "❓" },
  billing: { labelKey: "category.billing", emoji: "💳" },
};

const tTickets = (key: string) => i18n.t(key, { ns: "tickets" });
const tCommon = (key: string, opts?: Record<string, string | number>) =>
  i18n.t(key, { ns: "common", ...(opts ?? {}) });

// Helper functions
export function formatRelativeDate(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return tCommon("relative.justNow");
  if (diffMins < 60) return tCommon("relative.minutesAgo", { count: diffMins });
  if (diffHours < 24) return tCommon("relative.hoursAgo", { count: diffHours });
  if (diffDays < 7) return tCommon("relative.daysAgo", { count: diffDays });

  return date.toLocaleDateString(getIntlLocale(), {
    month: "short",
    day: "numeric",
  });
}

export function formatFullDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString(getIntlLocale(), {
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return tCommon("fileSize.bytes", { count: bytes });
  if (bytes < 1024 * 1024)
    return tCommon("fileSize.kb", { size: (bytes / 1024).toFixed(1) });
  return tCommon("fileSize.mb", {
    size: (bytes / (1024 * 1024)).toFixed(1),
  });
}

export function ticketStatusLabel(status: TicketStatus): string {
  return tTickets(STATUS_CONFIG[status].labelKey);
}

export function ticketPriorityLabel(priority: TicketPriority): string {
  return tTickets(PRIORITY_CONFIG[priority].labelKey);
}

export function ticketCategoryLabel(category: TicketCategory): string {
  return tTickets(CATEGORY_CONFIG[category].labelKey);
}

